import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format, parseISO, isValid } from 'date-fns';
import { queryKeys } from '../services/queryClient';
import { 
  getAvailableTimeSlots, 
  getAvailableProfessionals,
  checkTimeSlotAvailability,
  getTimeBlockings
} from '../services/availability';
import {
  fetchAvailableTimeSlotsWithCache
} from '../firebase/cachedAppointments';

/**
 * Hook para gerenciar dados de disponibilidade usando React Query
 * Substitui o antigo useAvailability com uma implementação mais eficiente
 * 
 * @param {string} salonId - ID do salão
 * @returns {Object} - Hooks e funções para gerenciar disponibilidade
 */
export const useAvailabilityQuery = (salonId) => {
  const queryClient = useQueryClient();
  
  /**
   * Converte qualquer entrada de data para o formato YYYY-MM-DD
   * @param {Date|string} date - Data para formatar
   * @returns {string} - Data formatada
   */
  const formatDate = (date) => {
    if (!date) return null;
    
    if (date instanceof Date) {
      return format(date, 'yyyy-MM-dd');
    }
    
    if (typeof date === 'string') {
      // Se já estiver no formato correto, retornar
      if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
        return date;
      }
      
      // Tentar converter de outros formatos
      const parsed = parseISO(date);
      if (isValid(parsed)) {
        return format(parsed, 'yyyy-MM-dd');
      }
    }
    
    return null;
  };
  
  /**
   * Normaliza o array de serviços para garantir consistência
   * @param {Array} services - Lista de serviços ou IDs
   * @returns {Array} - Lista de IDs de serviços
   */
  const normalizeServices = (services) => {
    console.log(`[useAvailabilityQuery] normalizeServices - input:`, services);
    console.log(`[useAvailabilityQuery] normalizeServices - tipo:`, typeof services);
    console.log(`[useAvailabilityQuery] normalizeServices - isArray:`, Array.isArray(services));
    
    // Se for nulo ou undefined, retornar array vazio
    if (!services) {
      console.log(`[useAvailabilityQuery] normalizeServices - retornando array vazio para input nulo`);
      return [];
    }
    
    // Se já for um array, mapear para extrair apenas os IDs
    if (Array.isArray(services)) {
      const result = services.map(service => 
        typeof service === 'object' ? service.id : service
      );
      console.log(`[useAvailabilityQuery] normalizeServices - array processado:`, result);
      return result;
    }
    
    // Se for um único valor (não-array), converter para array com este valor
    if (typeof services === 'object') {
      // Se for um objeto único, extrair o ID
      const id = services.id;
      console.log(`[useAvailabilityQuery] normalizeServices - convertendo objeto único para array:`, [id]);
      return [id];
    } 
    
    // Se for um número ou string (ID direto), retornar array com este ID
    console.log(`[useAvailabilityQuery] normalizeServices - convertendo valor único para array:`, [services]);
    return [services];
  };
  
  /**
   * Determina o motivo do bloqueio para feedback ao usuário
   * @param {Object} slot - Slot de horário
   * @param {Array} blockings - Lista de bloqueios
   * @returns {string|null} - Motivo do bloqueio ou null se não houver
   */
  const getBlockingReason = (slot, blockings) => {
    if (!slot || slot.available) return null;
    
    const [hours, minutes] = slot.time.split(':').map(Number);
    const slotStartMinutes = hours * 60 + minutes;
    const slotEndMinutes = slotStartMinutes + 30; // Duração padrão para visualização
    
    // Verificar se há bloqueio que sobrepõe este slot
    for (const block of blockings) {
      const [blockStartHours, blockStartMinutes] = block.horarioInicio.split(':').map(Number);
      const blockStartInMinutes = blockStartHours * 60 + blockStartMinutes;
      
      const [blockEndHours, blockEndMinutes] = block.horarioFim.split(':').map(Number);
      const blockEndInMinutes = blockEndHours * 60 + blockEndMinutes;
      
      if (slotStartMinutes < blockEndInMinutes && slotEndMinutes > blockStartInMinutes) {
        return block.titulo || 'Horário bloqueado';
      }
    }
    
    // Se tem conflitos com agendamentos
    if (slot.hasConflicts && slot.conflicts?.length > 0) {
      return 'Horário já reservado';
    }
    
    return 'Indisponível';
  };
  
  /**
   * Converte horário (HH:MM) para minutos desde o início do dia
   */
  const convertTimeToMinutes = (timeString) => {
    if (!timeString) return 0;
    const [hours, minutes] = timeString.split(':').map(Number);
    return hours * 60 + minutes;
  };
  
  /**
   * Hook para buscar horários disponíveis
   * Usa o sistema de cache interno do React Query e funções otimizadas
   * 
   * @param {Object} params - Parâmetros para a consulta
   * @returns {Object} - Resultado da consulta
   */
  const useAvailableTimeSlots = ({ date, services, excludeAppointmentId = null, enabled = true }) => {
    const formattedDate = formatDate(date);
    const serviceIds = normalizeServices(services);
    
    // Verificar se os parâmetros obrigatórios estão presentes
    const isQueryEnabled = enabled && !!salonId && !!formattedDate && serviceIds.length > 0;
    
    console.log(`[useAvailableTimeSlots] Consultando com excludeAppointmentId=${excludeAppointmentId}`, {
      date: formattedDate,
      services: serviceIds?.length || 0,
      enabled: isQueryEnabled
    });
    
    return useQuery({
      queryKey: queryKeys.availability.byServiceAndDate(salonId, serviceIds, formattedDate, excludeAppointmentId),
      queryFn: async () => {
        console.log(`[useAvailableTimeSlots] Executando query com excludeAppointmentId=${excludeAppointmentId}`);
        const slots = await fetchAvailableTimeSlotsWithCache(
          salonId, 
          formattedDate, 
          null, // Sem filtro de profissional nesta etapa
          serviceIds,
          queryClient,
          excludeAppointmentId // Passar ID de agendamento para excluir
        );
        console.log(`[useAvailableTimeSlots] Slots retornados: ${slots?.length || 0} para excludeAppointmentId=${excludeAppointmentId}`);
        return slots.filter(slot => !isTimeInPast(slot.time, date));
      },
      enabled: isQueryEnabled,
      staleTime: 2 * 60 * 1000, // 2 minutos
      cacheTime: 10 * 60 * 1000, // 10 minutos
      refetchOnWindowFocus: false,
    });
  };

  /**
   * Verifica se um horário está no passado
   * @param {string} time - Hora no formato HH:MM
   * @param {Date} date - Data a ser verificada
   * @returns {boolean} - True se o horário estiver no passado
   */
  const isTimeInPast = (time, date) => {
    // Verificar se a data informada é hoje
    const today = new Date();
    const selectedDate = new Date(date);
    
    const isToday = 
      today.getDate() === selectedDate.getDate() &&
      today.getMonth() === selectedDate.getMonth() &&
      today.getFullYear() === selectedDate.getFullYear();
    
    // Se não for hoje, horário não está no passado
    if (!isToday) return false;
    
    // Se for hoje, comparar com o horário atual
    const [hours, minutes] = time.split(':').map(Number);
    const currentHour = today.getHours();
    const currentMinute = today.getMinutes();
    
    // Verificar se está no passado
    if (hours < currentHour) return true;
    if (hours === currentHour && minutes <= currentMinute) return true;
    
    return false;
  };

  /**
   * Hook para buscar slots de tempo com informações detalhadas sobre bloqueios
   * 
   * @param {Object} params - Parâmetros para a consulta
   * @returns {Object} - Resultado da consulta com slots detalhados
   */
  const useDetailedTimeSlots = ({ date, services, excludeAppointmentId = null, enabled = true }) => {
    const formattedDate = formatDate(date);
    const serviceIds = normalizeServices(services);
    
    // Verificar se os parâmetros obrigatórios estão presentes
    const isQueryEnabled = enabled && !!salonId && !!formattedDate && serviceIds.length > 0;
    
    console.log(`[useDetailedTimeSlots] Consultando detalhados com excludeAppointmentId=${excludeAppointmentId}`, {
      date: formattedDate,
      services: serviceIds?.length || 0,
      enabled: isQueryEnabled
    });
    
    return useQuery({
      queryKey: queryKeys.availability.detailedByServiceAndDate(salonId, serviceIds, formattedDate, excludeAppointmentId),
      queryFn: async () => {
        console.log(`[useDetailedTimeSlots] Executando query detalhada com excludeAppointmentId=${excludeAppointmentId}`);
        // Buscar slots de tempo básicos
        const baseSlots = await fetchAvailableTimeSlotsWithCache(
          salonId, 
          formattedDate, 
          null, // Sem filtro de profissional inicialmente 
          serviceIds,
          queryClient,
          excludeAppointmentId // Passar ID de agendamento para excluir
        );
        
        console.log(`[useDetailedTimeSlots] Base slots: ${baseSlots?.length || 0} para excludeAppointmentId=${excludeAppointmentId}`);
        
        // Buscar bloqueios para enriquecer os dados
        const timeBlockings = await getTimeBlockings(salonId, formattedDate);
        
        // Enriquecer os slots com informações de bloqueio
        const enrichedSlots = baseSlots.map(slot => ({
          ...slot,
          isBlocked: timeBlockings.some(block => {
            // Verificar sobreposição entre slot e bloqueio
            const slotStartMinutes = convertTimeToMinutes(slot.time);
            const slotEndMinutes = slotStartMinutes + 30; // Duração padrão para visualização
            
            const blockStartMinutes = convertTimeToMinutes(block.horarioInicio);
            const blockEndMinutes = convertTimeToMinutes(block.horarioFim);
            
            return (slotStartMinutes < blockEndMinutes && 
                    slotEndMinutes > blockStartMinutes);
          }),
          blockingReason: getBlockingReason(slot, timeBlockings)
        }));
        
        // Filtrar horários no passado
        return enrichedSlots.filter(slot => !isTimeInPast(slot.time, date));
      },
      enabled: isQueryEnabled,
      staleTime: 60 * 1000, // 1 minuto
      refetchInterval: 2 * 60 * 1000, // Revalidar a cada 2 minutos
    });
  };
  
  /**
   * Hook para buscar profissionais disponíveis
   * @param {Object} params - Parâmetros para a consulta
   * @returns {Object} - Resultado da consulta
   */
  const useAvailableProfessionals = ({ date, time, services, enabled = true }) => {
    const formattedDate = formatDate(date);
    const serviceIds = normalizeServices(services);
    
    // Verificar se os parâmetros obrigatórios estão presentes
    const isQueryEnabled = enabled && !!salonId && !!formattedDate && !!time && serviceIds.length > 0;
    
    return useQuery({
      queryKey: queryKeys.availability.professionalsByServiceAndDateTime(
        salonId, 
        serviceIds, 
        formattedDate,
        time
      ),
      queryFn: () => getAvailableProfessionals(salonId, date, time, services),
      enabled: isQueryEnabled,
      staleTime: 2 * 60 * 1000, // 2 minutos
      cacheTime: 10 * 60 * 1000, // 10 minutos
      refetchOnWindowFocus: false,
    });
  };

  /**
   * Hook para consultar o estado atual de um slot específico
   * 
   * @param {Object} params - Parâmetros para a consulta
   * @returns {Object} - Resultado da consulta com status detalhado do slot
   */
  const useSlotStatus = ({ date, time, professional, services, excludeAppointmentId = null, enabled = true }) => {
    const formattedDate = formatDate(date);
    const serviceIds = normalizeServices(services);
    // Verificação defensiva para evitar acesso a propriedades de valores nulos
    const professionalId = professional ? (typeof professional === 'object' ? professional.id : professional) : null;
    
    // Garantir que todos os parâmetros necessários estejam disponíveis antes de executar a consulta
    const isQueryEnabled = enabled && !!salonId && !!formattedDate && !!time && !!professionalId && serviceIds.length > 0;
    
    return useQuery({
      queryKey: queryKeys.availability.slotStatus(salonId, formattedDate, time, professionalId, serviceIds, excludeAppointmentId),
      queryFn: () => checkTimeSlotAvailability(
        salonId, 
        formattedDate, 
        time, 
        professionalId, 
        serviceIds,
        excludeAppointmentId
      ),
      enabled: isQueryEnabled,
      staleTime: 30 * 1000, // 30 segundos
      refetchInterval: 60 * 1000, // Revalidar a cada minuto
    });
  };
  
  /**
   * Pré-carrega dados de disponibilidade para uma data
   * @param {Date|string} date - Data para pré-carregar
   * @param {Array} services - Serviços selecionados
   */
  const prefetchAvailability = async (date, services) => {
    const formattedDate = formatDate(date);
    const serviceIds = normalizeServices(services);
    
    if (!salonId || !formattedDate || serviceIds.length === 0) {
      console.warn(`[prefetchAvailability] Parâmetros inválidos`, {
        salonId,
        date: formattedDate,
        services: serviceIds?.length || 0
      });
      return;
    }
    
    console.log(`[prefetchAvailability] Pré-carregando para data=${formattedDate}`);
    
    // Prefetch de slots de horário disponíveis (básico)
    queryClient.prefetchQuery({
      queryKey: queryKeys.availability.byServiceAndDate(salonId, serviceIds, formattedDate, null),
      queryFn: () => fetchAvailableTimeSlotsWithCache(
        salonId, 
        formattedDate, 
        null, 
        serviceIds,
        queryClient
      ),
      staleTime: 2 * 60 * 1000
    });
    
    // Prefetch de slots detalhados
    queryClient.prefetchQuery({
      queryKey: queryKeys.availability.detailedByServiceAndDate(salonId, serviceIds, formattedDate),
      queryFn: async () => {
        const baseSlots = await fetchAvailableTimeSlotsWithCache(
          salonId, 
          formattedDate, 
          null, 
          serviceIds,
          queryClient
        );
        
        // Buscar bloqueios para enriquecer os dados
        const timeBlockings = await getTimeBlockings(salonId, formattedDate);
        
        // Enriquecer os slots com informações de bloqueio
        const enrichedSlots = baseSlots.map(slot => ({
          ...slot,
          isBlocked: timeBlockings.some(block => {
            // Verificar sobreposição entre slot e bloqueio
            const slotStartMinutes = convertTimeToMinutes(slot.time);
            const slotEndMinutes = slotStartMinutes + 30; // Duração padrão para visualização
            
            const blockStartMinutes = convertTimeToMinutes(block.horarioInicio);
            const blockEndMinutes = convertTimeToMinutes(block.horarioFim);
            
            return (slotStartMinutes < blockEndMinutes && 
                    slotEndMinutes > blockStartMinutes);
          }),
          blockingReason: getBlockingReason(slot, timeBlockings)
        }));
        
        // Filtrar horários no passado
        return enrichedSlots.filter(slot => !isTimeInPast(slot.time, date));
      },
      staleTime: 60 * 1000
    });
    
    console.log(`[prefetchAvailability] Prefetch iniciado para data=${formattedDate}`);
  };
  
  /**
   * Limpa o cache de disponibilidade
   * @param {string} date - Data específica para limpar (opcional)
   */
  const invalidateAvailability = async (date = null) => {
    if (date) {
      const formattedDate = formatDate(date);
      
      // Invalidar apenas consultas relacionadas a esta data
      await queryClient.invalidateQueries({
        predicate: (query) => 
          query.queryKey[0] === 'availability' && 
          query.queryKey[2] === 'date' && 
          query.queryKey[3] === formattedDate
      });
      
      // Invalidar também os agendamentos para essa data
      await queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] === 'appointments' &&
          (query.queryKey[2] === formattedDate || query.queryKey[3] === formattedDate)
      });
    } else {
      // Invalidar todas as consultas de disponibilidade
      await queryClient.invalidateQueries({
        queryKey: queryKeys.availability.all,
      });
      
      // Invalidar também todos os agendamentos
      await queryClient.invalidateQueries({
        queryKey: ['appointments'],
      });
    }
  };
  
  /**
   * Mutation para atualizar a disponibilidade quando ocorrer mudanças
   * (como agendamentos novos)
   */
  const invalidateAvailabilityMutation = useMutation({
    mutationFn: (date) => invalidateAvailability(date),
    onSuccess: () => {
      // Cache invalidado com sucesso
    },
  });
  
  return {
    useAvailableTimeSlots,
    useAvailableProfessionals,
    useDetailedTimeSlots,
    useSlotStatus,
    prefetchAvailability,
    invalidateAvailability,
    invalidateAvailabilityMutation,
  };
}; 